import React from "react";
import { range } from "lodash";
import { usePreviousImmediate } from "rooks";
import PropTypes from "prop-types";
import { classNames } from "@gonoodle/gn-universe-ui";

export default function ProgressBar({ current, max, limit, target }) {
  const cap = limit || max;
  const currentUnderCap = Math.min(current, cap);
  const remaining = max - currentUnderCap;

  const previousCurrent = usePreviousImmediate(current);

  // Only animate progress bar when points increment.
  const isAnimated = current >= previousCurrent;

  const progressDescription = remaining
    ? `${currentUnderCap} point${
        currentUnderCap === 1 ? "" : "s"
      }. ${remaining} point${remaining === 1 ? "" : "s"} remaining to ${target}`
    : `${currentUnderCap} point${currentUnderCap === 1 ? "" : "s"}`;

  return (
    <div
      className="w-full flex relative"
      aria-valuemin={0}
      aria-valuemax={cap}
      aria-valuenow={currentUnderCap}
      aria-valuetext={progressDescription}
      role="progressbar"
    >
      {range(1, cap + 1).map((point) => (
        <div
          className="flex flex-1 justify-end items-center p-3 z-10 max-sm:px-2"
          key={point}
        >
          {!limit && point === max ? (
            <div
              className={classNames(
                "rounded-full flex items-center justify-center w-2 h-2 sm:w-4 sm:h-4 lg:w-auto lg:h-auto transition-colors duration-1000",
                point === currentUnderCap ? "bg-white" : "bg-purple",
              )}
              aria-hidden
            >
              <p
                className={classNames(
                  "text-white gn-text-sm font-bold px-3 py-2 text-center max-lg:hidden transition-colors whitespace-nowrap duration-1000",
                  point === currentUnderCap ? "text-purple" : "text-white",
                )}
              >
                {target}
              </p>
            </div>
          ) : (
            <div
              className={classNames(
                "rounded-full relative w-2 h-2 sm:w-3 sm:h-3 lg:w-6 lg:h-6",
                point <= currentUnderCap ? "bg-white" : "bg-purple",
              )}
              aria-hidden
            >
              <div
                className={classNames(
                  "absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2",
                  "transition-transform duration-500 scale-0",
                  point === currentUnderCap && "scale-100",
                  "w-5 h-5 sm:w-6 sm:h-6 lg:w-9 lg:h-9",
                  "bg-white rounded-full",
                  "flex items-center justify-center",
                )}
              >
                {point === currentUnderCap && (
                  <p className="text-purple font-bold text-center text-sm lg:text-lg">
                    {currentUnderCap}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      <div
        className={classNames(
          "absolute top-0 bottom-0 left-0 bg-gradient-to-b from-purple-400 to-purple rounded-full w-[var(--progress)]",
          isAnimated && "transition-all duration-1000",
          currentUnderCap === cap &&
            "shadow-purple shadow-[0px_0px_22px_3px_var(--tw-shadow-color)]",
        )}
        style={{
          "--progress": `${(currentUnderCap / cap) * 100}%`,
        }}
      />

      <div className="absolute inset-0 bg-purple opacity-30 rounded-full" />
    </div>
  );
}

ProgressBar.propTypes = {
  current: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  limit: PropTypes.number,
  target: PropTypes.string.isRequired,
};
